/**
 * 品目追加（SPEC §7.7: 必須は名前のみ）。
 * カテゴリと想定使用日数は既定値で通せるようにしておく。
 */
import { useState, type ReactNode } from 'react';
import type { NewItemInput } from '../lib/actions';
import { Sheet } from './Sheet';

/** SPEC §15-2 のカテゴリ初期シード。カテゴリ管理 UI は Phase 4。 */
const CATEGORY_SEEDS = ['日用品', '衛生用品', 'キッチン', 'コーヒー', 'その他'];

export interface AddItemModalProps {
  onClose: () => void;
  onSubmit: (input: NewItemInput) => void;
}

export function AddItemModal({ onClose, onSubmit }: AddItemModalProps): ReactNode {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [days, setDays] = useState('30');
  const [detailOpen, setDetailOpen] = useState(false);

  const trimmed = name.trim();
  const daysValid = /^\d{1,4}$/.test(days) && Number(days) >= 1 && Number(days) <= 3650;
  const canSave = trimmed !== '' && trimmed.length <= 200 && daysValid;

  const submit = (): void => {
    if (!canSave) return;
    onSubmit({
      name: trimmed,
      category: category.trim() === '' ? null : category.trim(),
      defaultDurationDays: Number(days),
    });
  };

  return (
    <Sheet
      title="品目を追加"
      onClose={onClose}
      footer={
        <button type="button" className="btn-primary w-full" disabled={!canSave} onClick={submit}>
          追加
        </button>
      }
    >
      <form
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div>
          <label className="mb-1.5 block text-sm font-medium text-zinc-400" htmlFor="item-name">
            品目名<span className="ml-1 text-orange-400">必須</span>
          </label>
          <input
            id="item-name"
            className="field text-lg"
            autoFocus
            placeholder="シャンプー"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div>
          <span className="mb-1.5 block text-sm font-medium text-zinc-400">カテゴリ（任意）</span>
          <div className="mb-2 flex flex-wrap gap-1.5">
            {CATEGORY_SEEDS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCategory((cur) => (cur === c ? '' : c))}
                className={`chip border ${
                  category === c
                    ? 'border-orange-500 bg-orange-500/15 text-orange-300'
                    : 'border-ink-700 bg-ink-800 text-zinc-400'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <input
            className="field"
            placeholder="自由入力も可"
            value={category}
            aria-label="カテゴリ"
            onChange={(e) => setCategory(e.target.value)}
          />
        </div>

        {detailOpen ? (
          <div>
            <label className="mb-1.5 block text-sm font-medium text-zinc-400" htmlFor="item-days">
              想定使用日数
            </label>
            <input
              id="item-days"
              className="field tabular-nums"
              inputMode="numeric"
              pattern="[0-9]*"
              value={days}
              onChange={(e) => setDays(e.target.value.replace(/[^\d]/g, ''))}
            />
            <p className="mt-1.5 text-xs text-zinc-500">
              使い切り履歴が貯まるまでの按分・予測に使う既定値（SPEC §6.1）。
            </p>
          </div>
        ) : (
          <button
            type="button"
            className="text-sm text-zinc-500 underline-offset-4 hover:text-zinc-300 hover:underline"
            onClick={() => setDetailOpen(true)}
          >
            ＋ 想定使用日数を変更（既定 30 日）
          </button>
        )}

        <button type="submit" className="hidden" aria-hidden />
      </form>
    </Sheet>
  );
}
